import { GlobalAccessLevel } from '@/security/constants/GlobalAccessLevel';
import { AccessTargetType } from '@/security/types/AccessTarget';
import { DataAccess, ProductDataAccess, ProjectDataAccess } from '@/security/types/DataAccess';

// For a given access level the title is either a single string (when it doesn't depend on the data access,
// e.g. Restricted) or a map keyed by the data access level (a numeric enum: Product- or ProjectDataAccess).
type TitleByDataAccess = Partial<Record<number, string>>;
type AccessStatusTitles = Partial<Record<GlobalAccessLevel, string | TitleByDataAccess>>;

const PRODUCT_TITLES: AccessStatusTitles = {
  [GlobalAccessLevel.Restricted]: 'Product is not available outside the company.',
  [GlobalAccessLevel.Unlisted]: {
    [ProductDataAccess.HideAllDesignData]:
      'Product is not available in Catalog (only by direct link). All cut design data is hidden.',
    [ProductDataAccess.HideModelReports]:
      'Product is not visible in Catalog (only by direct link). 3D model and reports are hidden.',
    [ProductDataAccess.ShowAllData]:
      'Product is not visible in Catalog (only by direct link). All cut design data is available.',
  },
  [GlobalAccessLevel.Public]: {
    [ProductDataAccess.HideAllDesignData]: 'Product is available to anyone. All cut design data is hidden.',
    [ProductDataAccess.HideModelReports]: 'Product is available to anyone. 3D models and reports are hidden.',
    [ProductDataAccess.ShowAllData]: 'Product is available to anyone. All cut design data is available.',
  },
};

const PROJECT_TITLES: AccessStatusTitles = {
  [GlobalAccessLevel.Restricted]: 'Project is not available outside the company.',
  [GlobalAccessLevel.Public]: {
    [ProjectDataAccess.HideProjectSource]: 'Project is available to anyone. HPC source is hidden.',
    [ProjectDataAccess.ShowAllData]: 'Project is available to anyone. HPC source is available.',
  },
};

const DRIVE_PROJECT_TITLES: AccessStatusTitles = {
  [GlobalAccessLevel.Restricted]: 'Multistage projects are not available outside the company.',
  [GlobalAccessLevel.Public]: {
    [ProductDataAccess.HideAllDesignData]:
      'Multistage projects are available to anyone. HPC source and all cut design data are hidden.',
    [ProductDataAccess.HideModelReports]:
      'Multistage projects are available to anyone. HPC source, Diamond 3D models, and reports are hidden.',
    [ProductDataAccess.ShowAllData]:
      'Multistage projects are available to anyone. HPC source and all Diamond cut design data are available.',
  },
};

const DRIVE_FOLDER_TITLES: AccessStatusTitles = {
  [GlobalAccessLevel.Restricted]: 'Folder is available only to you.',
  [GlobalAccessLevel.Public]: 'Folder is available via direct link to anyone.',
};

const COLLECTION_TITLES: AccessStatusTitles = {
  [GlobalAccessLevel.Restricted]: 'Collection is available only to you.',
  [GlobalAccessLevel.Public]: 'Collection is available via direct link to anyone.',
};

const ACCESS_STATUS_TITLES: Record<AccessTargetType, AccessStatusTitles> = {
  product: PRODUCT_TITLES,
  project: PROJECT_TITLES,
  driveProject: DRIVE_PROJECT_TITLES,
  driveFolder: DRIVE_FOLDER_TITLES,
  collection: COLLECTION_TITLES,
};

export function getAccessStatusTitle(
  type: AccessTargetType,
  access: GlobalAccessLevel,
  dataAccess?: DataAccess,
): string {
  const title = ACCESS_STATUS_TITLES[type][access];

  if (typeof title === 'string') {
    return title;
  }

  if (title && dataAccess !== undefined) {
    return title[dataAccess] ?? '';
  }

  return '';
}
